import {
    Component,
    DestroyRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
} from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { DocumentDownloadService } from '../../services/document-download.service';
import { DownloadFileService } from '../../services/download-file.service';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';

@Component({
    selector: 'apcam-jsonviewer',
    templateUrl: './json-viewer.component.html',
    styleUrls: ['./json-viewer.component.scss'],
    imports: [LoadingSpinnerComponent],
})
export class JsonViewerComponent implements OnInit, OnChanges {
    /**
     * Observable used for downloading Json file.
     */
    @Input()
    download: Subject<void> = new Subject();

    /**
     * Name of the viewed Json.
     */
    @Input()
    fileName: string = '';

    /**
     * URL from which the viewed Json should be downloaded.
     */
    @Input()
    jsonUrl: string = '';

    /**
     * Signal emitter for passing on information whether the displayed JSON is ready to be displayed or not.
     */
    @Output()
    jsonLoaded: EventEmitter<boolean> = new EventEmitter();

    public jsonText: string | null = null;

    constructor(
        private readonly destroyRef: DestroyRef,
        private readonly downloadService: DocumentDownloadService,
        private readonly downloadFileService: DownloadFileService
    ) {}

    ngOnChanges(): void {
        this.jsonText = null;
        this.getTextData(this.jsonUrl);
    }

    ngOnInit(): void {
        this.getTextData(this.jsonUrl);

        this.download
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.downloadJson();
            });
    }

    /**
     * Triggers the download action into the local filesystem of the displayed JSON file.
     */
    async downloadJson() {
        if (this.jsonText) {
            const file = new Blob([this.jsonText], {
                type: 'application/json',
            });
            this.downloadFileService.downloadBlobFile(file, this.fileName);
        }
    }

    /**
     * Retrieves the JSON content to be displayed.
     * @param url URL whence the text shall be downloaded
     */
    getTextData(url: string) {
        this.downloadService.downloadFileToBuffer(url).subscribe((data) => {
            let bufferSize = data.byteLength;
            this.jsonText = new TextDecoder().decode(
                new Uint8Array(data, 0, bufferSize)
            );
            this.jsonLoaded.emit(true);
        });
    }
}
